import { useCallback, useEffect, useRef, useState } from "react"

import { useUser } from "@clerk/nextjs"
import { toast } from "@coval/ui/toast"

import { logger } from "@/lib/logger"
import { annotationHasValue, type Annotation } from "@/types/api/Annotation"

import { useMergedAnnotationOwner } from "../contexts/MergedAnnotationOwnerContext"
import { useReviewSaveErrorHandler } from "../contexts/ReviewSaveErrorContext"
import { MISSING_CURRENT_USER_EMAIL_TOAST } from "../utils/annotationSaveError"
import {
  convertStringToFloat,
  createOptimisticAnnotation,
  resolveAnnotationOwnerEmail,
  setValuesEqual,
} from "../utils/humanFeedbackUtils"
import { useHumanFeedbackAnnotationMutations } from "./useHumanFeedbackAnnotationMutations"

const log = logger("HumanFeedbackUpdate")

export interface UseHumanFeedbackUpdateProps {
  simulationOutputId: string
  metricId: string
  initialAnnotation?: Annotation | null
  projectId?: string // Optional project ID for optimistic updates
  /**
   * The email of the user who actually owns the annotation row backing
   * `initialAnnotation` in the database. When the hook is fed a "merged latest"
   * annotation in collaborative projects (where another reviewer's row is
   * displayed in the current user's queue), `initialAnnotation.assignee` may be
   * spoofed to the current user for display purposes. Pass this to keep the
   * hook's "is this my row?" check honest so it knows to snapshot-create
   * instead of updating in place.
   */
  actualOwnerEmail?: string | null
  /** Fires when the value is set/cleared (before save) so callers can flip vote-gated UI instantly. */
  onValueOptimisticallyChanged?: (hasValue: boolean) => void
  /** Registers this controller's queued writes with an enclosing submission barrier. */
  onSaveStarted?: () => (succeeded?: boolean) => void
}

export interface UseHumanFeedbackUpdateReturn {
  currentAnnotation: Annotation | null
  isUpdating: boolean
  /** Resolves false when the save failed and the annotation was rolled back. */
  handleValueUpdate: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
    jsonValue?: object | null,
  ) => Promise<boolean>
  handleExplanationUpdate: (explanation: string | null) => Promise<boolean>
  /** Sync local state with annotation from server (e.g. after external mutation). */
  syncAnnotationFromServer: (annotation: Annotation) => void
}

export const useHumanFeedbackUpdate = ({
  simulationOutputId,
  metricId,
  initialAnnotation,
  projectId,
  actualOwnerEmail,
  onValueOptimisticallyChanged,
  onSaveStarted,
}: UseHumanFeedbackUpdateProps): UseHumanFeedbackUpdateReturn => {
  // Held in a ref so the callback identity doesn't churn handleValueUpdate.
  const onValueOptimisticallyChangedRef = useRef(onValueOptimisticallyChanged)
  onValueOptimisticallyChangedRef.current = onValueOptimisticallyChanged
  const onSaveStartedRef = useRef(onSaveStarted)
  onSaveStartedRef.current = onSaveStarted
  const { user } = useUser()
  const currentUserEmail = user?.emailAddresses?.[0]?.emailAddress ?? null
  const contextOwnerEmail = useMergedAnnotationOwner(
    simulationOutputId,
    metricId,
  )
  const resolvedOwnerEmail =
    actualOwnerEmail !== undefined ? actualOwnerEmail : contextOwnerEmail
  const onSaveError = useReviewSaveErrorHandler()

  // Local state for immediate UI updates
  const [localAnnotation, setLocalAnnotation] = useState<Annotation | null>(
    initialAnnotation || null,
  )
  const [isUpdating, setIsUpdating] = useState(false)
  const latestAnnotation = useRef<Annotation | null>(initialAnnotation || null)
  const operationQueue = useRef<Promise<void>>(Promise.resolve())
  const pendingOperationCount = useRef(0)
  const previousMetricId = useRef<string>(metricId)
  const previousSimulationOutputId = useRef<string>(simulationOutputId)
  const previousInitialAnnotationId = useRef<string | undefined>(
    initialAnnotation?.id,
  )

  const setCurrentAnnotation = useCallback((annotation: Annotation | null) => {
    latestAnnotation.current = annotation
    setLocalAnnotation(annotation)
  }, [])

  const queueOperation = useCallback(
    (
      operation: () => Promise<{
        result: boolean
        succeeded: boolean
      }>,
    ): Promise<boolean> => {
      pendingOperationCount.current += 1
      setIsUpdating(true)
      const endSave = onSaveStartedRef.current?.()

      const result = operationQueue.current.then(operation, operation)
      operationQueue.current = result.then(
        () => undefined,
        () => undefined,
      )

      return result
        .then(({ result: operationResult, succeeded }) => {
          endSave?.(succeeded)
          return operationResult
        })
        .catch((error: unknown) => {
          endSave?.(false)
          throw error
        })
        .finally(() => {
          pendingOperationCount.current -= 1
          if (pendingOperationCount.current === 0) {
            setIsUpdating(false)
          }
        })
    },
    [],
  )

  const { createAnnotation, updateAnnotation } =
    useHumanFeedbackAnnotationMutations({
      initialAnnotation,
      latestAnnotation,
      setCurrentAnnotation,
      onValueOptimisticallyChanged: onValueOptimisticallyChangedRef,
      projectId,
    })

  // Use local annotation if available, otherwise fall back to initial annotation
  const currentAnnotation = localAnnotation || initialAnnotation || null

  // Reset local annotation when switching context, but avoid flicker during optimistic updates
  useEffect(() => {
    // Skip resets while an optimistic update is in flight
    if (pendingOperationCount.current > 0 || isUpdating) {
      return
    }

    // Skip if the current local annotation is a temp optimistic one
    if (localAnnotation?.id?.startsWith("temp-")) {
      return
    }

    // If the incoming annotation has the same id as our local one, no reset
    if (
      initialAnnotation?.id &&
      localAnnotation?.id === initialAnnotation.id &&
      localAnnotation.simulation_output_id === simulationOutputId &&
      localAnnotation.metric_id === metricId
    ) {
      return
    }

    // Only reset when context (metric or simulation) changes
    const contextChanged =
      previousMetricId.current !== metricId ||
      previousSimulationOutputId.current !== simulationOutputId ||
      previousInitialAnnotationId.current !== initialAnnotation?.id
    if (!contextChanged) {
      return
    }

    setCurrentAnnotation(initialAnnotation || null)

    previousMetricId.current = metricId
    previousSimulationOutputId.current = simulationOutputId
    previousInitialAnnotationId.current = initialAnnotation?.id
  }, [
    initialAnnotation,
    localAnnotation,
    metricId,
    simulationOutputId,
    isUpdating,
    setCurrentAnnotation,
  ])

  const handleValueUpdate = useCallback(
    async (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
      jsonValue?: object | null,
    ) => {
      const jsonAnnotationValue = jsonValue as
        | Record<string, unknown>
        | null
        | undefined

      // Convert string values to float values for backwards compatibility
      const convertedFloatValue =
        floatValue ?? convertStringToFloat(stringValue)

      const saveValue = async () => {
        const currentAnnotation = latestAnnotation.current
        const hasPersistedRow =
          !!currentAnnotation?.id && !currentAnnotation.id.startsWith("temp-")
        const ownerEmail = resolveAnnotationOwnerEmail(
          currentAnnotation?.assignee ?? null,
          resolvedOwnerEmail,
        )
        const isMine = !!currentUserEmail && ownerEmail === currentUserEmail

        // Guard against no-op saves on someone else's row. Widgets like
        // AnnotationSelector fire onValueChange even when the user just clicks
        // an already-selected button, and NotesCell saves on blur regardless of
        // whether the text changed. Under snapshot-on-edit that would silently
        // record the current user as a reviewer of a row they never intended to
        // touch. Skip the write when nothing would change.
        if (hasPersistedRow && !isMine) {
          const jsonUnchanged =
            jsonValue === undefined ||
            JSON.stringify(jsonAnnotationValue ?? null) ===
              JSON.stringify(currentAnnotation?.ground_truth_json ?? null)
          if (
            convertedFloatValue ===
              (currentAnnotation?.ground_truth_float_value ?? null) &&
            stringValue ===
              (currentAnnotation?.ground_truth_string_value ?? null) &&
            setValuesEqual(
              setValue,
              currentAnnotation?.ground_truth_set_value ?? null,
            ) &&
            jsonUnchanged
          ) {
            return { result: false, succeeded: true }
          }
        }

        const willCreateAnnotation = !hasPersistedRow || !isMine
        if (willCreateAnnotation && !currentUserEmail) {
          log.error("Cannot create annotation without a current user email")
          toast.danger(MISSING_CURRENT_USER_EMAIL_TOAST)
          return { result: false, succeeded: false }
        }

        try {
          if (hasPersistedRow && isMine) {
            // Update my own existing annotation in place
            const updatedAnnotation = {
              ...currentAnnotation,
              ground_truth_float_value: convertedFloatValue,
              ground_truth_string_value: stringValue,
              ground_truth_set_value: setValue,
              ...(jsonValue !== undefined && {
                ground_truth_json: jsonAnnotationValue,
              }),
            }
            setCurrentAnnotation(updatedAnnotation)
            onValueOptimisticallyChangedRef.current?.(
              annotationHasValue(updatedAnnotation),
            )

            await updateAnnotation.mutateAsync({
              annotationId: currentAnnotation.id,
              groundTruthFloatValue: convertedFloatValue,
              groundTruthStringValue: stringValue,
              groundTruthSetValue: setValue,
              ...(projectId !== undefined ? { projectId } : {}),
              ...(jsonValue !== undefined && {
                groundTruthJson: jsonAnnotationValue,
              }),
            })
          } else {
            // Create a new annotation. In collaborative projects this is the
            // "snapshot on edit" path: we clone the previous reviewer's row so
            // the new row stands on its own (and so individual project queues
            // for the previous reviewer keep their original data intact). When
            // there's no persisted row at all, the snapshot is empty and this
            // collapses to a normal first-time create.
            const snapshotBase = hasPersistedRow ? currentAnnotation : null
            const optimisticAnnotation = createOptimisticAnnotation(
              simulationOutputId,
              metricId,
              snapshotBase,
              {
                ground_truth_float_value: convertedFloatValue,
                ground_truth_string_value: stringValue,
                ground_truth_set_value: setValue,
                ...(jsonValue !== undefined && {
                  ground_truth_json: jsonAnnotationValue,
                }),
              },
            )
            // The new row is owned by the current user, not the snapshot source.
            if (currentUserEmail) {
              optimisticAnnotation.assignee = currentUserEmail
            }

            setCurrentAnnotation(optimisticAnnotation)
            onValueOptimisticallyChangedRef.current?.(
              annotationHasValue(optimisticAnnotation),
            )

            await createAnnotation.mutateAsync({
              simulationOutputId,
              metricId,
              assignee: currentUserEmail,
              ground_truth_float_value: convertedFloatValue,
              ground_truth_string_value: stringValue,
              ground_truth_set_value: setValue,
              // Carry forward fields the user didn't touch from the snapshot
              // base so the new row is self-contained.
              ground_truth_subvalues_by_timestamp:
                snapshotBase?.ground_truth_subvalues_by_timestamp ?? null,
              annotations: snapshotBase?.annotations ?? null,
              ...(projectId !== undefined ? { projectId } : {}),
              ground_truth_json:
                jsonValue !== undefined
                  ? (jsonAnnotationValue ?? null)
                  : (snapshotBase?.ground_truth_json ?? null),
            })
          }
          return { result: true, succeeded: true }
        } catch (error) {
          // Rollback and the toast run in the mutation onError callbacks.
          log.error("Unexpected error in handleValueUpdate", error)
          onSaveError?.(simulationOutputId, error)
          return { result: false, succeeded: false }
        }
      }

      let succeeded = false
      await queueOperation(async () => {
        const outcome = await saveValue()
        succeeded = outcome.succeeded
        return outcome
      })
      return succeeded
    },
    [
      currentUserEmail,
      resolvedOwnerEmail,
      onSaveError,
      updateAnnotation,
      createAnnotation,
      simulationOutputId,
      metricId,
      projectId,
      queueOperation,
      setCurrentAnnotation,
    ],
  )

  const handleExplanationUpdate = useCallback(
    async (explanation: string | null) => {
      return queueOperation(async () => {
        const currentAnnotation = latestAnnotation.current
        const hasPersistedRow =
          !!currentAnnotation?.id && !currentAnnotation.id.startsWith("temp-")
        const ownerEmail = resolveAnnotationOwnerEmail(
          currentAnnotation?.assignee ?? null,
          resolvedOwnerEmail,
        )
        const isMine = !!currentUserEmail && ownerEmail === currentUserEmail

        // Guard: don't snapshot-create a new row on someone else's annotation
        // when the note is unchanged (NotesCell saves on blur regardless).
        if (hasPersistedRow && !isMine) {
          const currentNote =
            currentAnnotation?.annotations?.custom_explanation ?? null
          const normalizedIncoming = explanation ?? null
          if (currentNote === normalizedIncoming) {
            return { result: false, succeeded: true }
          }
        }

        const willCreateAnnotation = !hasPersistedRow || !isMine
        if (willCreateAnnotation && !currentUserEmail) {
          log.error("Cannot create annotation without a current user email")
          toast.danger(MISSING_CURRENT_USER_EMAIL_TOAST)
          return { result: false, succeeded: false }
        }

        try {
          if (hasPersistedRow && isMine) {
            // Update my own existing annotation in place
            const updatedAnnotation = {
              ...currentAnnotation,
              annotations: {
                ...currentAnnotation.annotations,
                custom_explanation: explanation,
              },
            }
            setCurrentAnnotation(updatedAnnotation)

            await updateAnnotation.mutateAsync({
              annotationId: currentAnnotation.id,
              annotations: {
                ...currentAnnotation.annotations,
                custom_explanation: explanation,
              },
              ...(projectId !== undefined ? { projectId } : {}),
            })
          } else {
            // Snapshot-on-edit: clone the prior reviewer's row (if any) so the
            // new row contains the value, json, etc. they had set, with only
            // the note overlaid.
            const snapshotBase = hasPersistedRow ? currentAnnotation : null
            const optimisticAnnotation = createOptimisticAnnotation(
              simulationOutputId,
              metricId,
              snapshotBase,
              {
                annotations: {
                  ...snapshotBase?.annotations,
                  custom_explanation: explanation,
                },
              },
            )
            if (currentUserEmail) {
              optimisticAnnotation.assignee = currentUserEmail
            }

            setCurrentAnnotation(optimisticAnnotation)

            await createAnnotation.mutateAsync({
              simulationOutputId,
              metricId,
              assignee: currentUserEmail,
              ground_truth_float_value:
                snapshotBase?.ground_truth_float_value ?? null,
              ground_truth_string_value:
                snapshotBase?.ground_truth_string_value ?? null,
              ground_truth_set_value:
                snapshotBase?.ground_truth_set_value ?? null,
              ground_truth_subvalues_by_timestamp:
                snapshotBase?.ground_truth_subvalues_by_timestamp ?? null,
              ground_truth_json: snapshotBase?.ground_truth_json ?? null,
              annotations: {
                ...snapshotBase?.annotations,
                custom_explanation: explanation,
              },
              ...(projectId !== undefined ? { projectId } : {}),
            })
          }
          return { result: true, succeeded: true }
        } catch (error) {
          // Rollback and the toast run in the mutation onError callbacks.
          log.error("Unexpected error in handleExplanationUpdate", error)
          onSaveError?.(simulationOutputId, error)
          return { result: false, succeeded: false }
        }
      })
    },
    [
      currentUserEmail,
      resolvedOwnerEmail,
      onSaveError,
      updateAnnotation,
      createAnnotation,
      simulationOutputId,
      metricId,
      projectId,
      queueOperation,
      setCurrentAnnotation,
    ],
  )

  const syncAnnotationFromServer = useCallback(
    (annotation: Annotation) => {
      setCurrentAnnotation(annotation)
    },
    [setCurrentAnnotation],
  )

  return {
    currentAnnotation, //What should be displayed
    isUpdating,
    handleValueUpdate,
    handleExplanationUpdate,
    syncAnnotationFromServer,
  }
}
